import { Injectable } from '@angular/core';
import { DELETE, MANAGE, VIEW } from '@core/constants/permission.const';
import { CurrentUserService } from './currentuser.service';
import { AppModulePath } from '@core/constants/app-module-path.const';
import { ICurrentUserModel } from '@models/auth/auth.model';
import { UserType } from '@core/enums/type.enum';
import { AppModule } from '@core/enums/app-module.enum';
import { Routing } from '@core/constants/routing.const';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  constructor(private currentUserService: CurrentUserService) {}

  checkAppModuleAccess = (path: string) => {
    let matchedModule = '';
    let longestMatchLength = 0;

    const cleanPath = path ? path.split('?')[0].split('#')[0] : '';

    for (const module of AppModulePath) {
      if (module?.accessiblePaths?.length > 0) {
        for (const accessiblePath of module.accessiblePaths) {
          if (
            cleanPath.includes(accessiblePath) &&
            accessiblePath.length > longestMatchLength
          ) {
            longestMatchLength = accessiblePath.length;
            matchedModule = module.moduleName;
          }
        }
      }
    }

    if (!matchedModule) return true;
    if (
      matchedModule === AppModule.Profile ||
      matchedModule === AppModule.Dashboard
    )
      return true;

    const currentUser: ICurrentUserModel = this.currentUserService.get();
    if (currentUser?.userTypeId === UserType.SuperAdmin) return true;

    if (
      (currentUser?.userTypeId === UserType.Receptionist ||
        currentUser?.userTypeId === UserType.Practitioner) &&
      cleanPath.includes(Routing.Admin)
    ) {
      return false;
    }

    const permission: any = currentUser?.permissions?.find(
      (p: any) =>
        p?.ModuleName?.toLowerCase() === matchedModule?.toLowerCase() ||
        p?.moduleName?.toLowerCase() === matchedModule?.toLowerCase()
    );
    return (permission?.CanView ?? permission?.canView) ?? false;
  };

  checkAppModulePermission = (module: string, permission: string) => {
    if (module === AppModule.Profile || module === AppModule.Dashboard)
      return true;
    const currentUser: ICurrentUserModel = this.currentUserService.get();
    if (currentUser?.userTypeId === UserType.SuperAdmin) return true;
    const rolPermission: any = currentUser?.permissions?.find(
      (p: any) =>
        p?.ModuleName?.toLowerCase() === module?.toLowerCase() ||
        p?.moduleName?.toLowerCase() === module?.toLowerCase()
    );

    if (!rolPermission) return false;

    const canView = rolPermission?.CanView ?? rolPermission?.canView ?? false;
    const canManage = rolPermission?.CanManage ?? rolPermission?.canManage ?? false;
    const canDelete = rolPermission?.CanDelete ?? rolPermission?.canDelete ?? false;

    switch (permission) {
      case VIEW:
        return !!canView;
      case MANAGE:
        return !!canManage;
      case DELETE:
        return !!canDelete;
    }
    return false;
  };

  checkAppModuleActionPermissions = (
    module: string,
    permissions: string[]
  ): boolean => {
    if (module === AppModule.Profile || module === AppModule.Dashboard)
      return true;
    const currentUser: ICurrentUserModel = this.currentUserService.get();
    if (currentUser?.userTypeId === UserType.SuperAdmin) return true;

    const rolPermission: any = currentUser?.permissions?.find(
      (p: any) =>
        p?.ModuleName?.toLowerCase() === module?.toLowerCase() ||
        p?.moduleName?.toLowerCase() === module?.toLowerCase()
    );

    if (!rolPermission) return false;

    const canView = rolPermission?.CanView ?? rolPermission?.canView ?? false;
    const canManage = rolPermission?.CanManage ?? rolPermission?.canManage ?? false;
    const canDelete = rolPermission?.CanDelete ?? rolPermission?.canDelete ?? false;

    return permissions.some(
      (permission) =>
        (permission === VIEW && canView) ||
        (permission === MANAGE && canManage) ||
        (permission === DELETE && canDelete)
    );
  };
}
